import { FieldType } from '@databases/model/field';
import { MutableValue, Value, ValueJSON } from '@databases/model/value';
import { Op } from 'quill-delta';
import * as Y from 'yjs';

import { DisposableElement, WrappedYType } from '../helpers/types';
import { ValueProperties } from '../y-entry';
import { YFieldType } from '../y-field';

export class YTextValue
  implements
    Value<FieldType.Text>,
    MutableValue<FieldType.Text>,
    WrappedYType,
    DisposableElement
{
  #text: Y.Text | undefined;
  constructor(private input: Y.Map<any>, private attach?: (map: Y.Map<any>) => void) {
    input.observe(this.onValueChanged);

    const ref = input.get(ValueProperties.value);
    if (ref instanceof Y.Text) {
      this.#text = ref;
    }
  }

  private onValueChanged = (e: Y.YMapEvent<any>) => {
    if (e.keysChanged.has(ValueProperties.value)) {
      const val = this.input.get(ValueProperties.value);
      if (val instanceof Y.Text) {
        this.#text = val;
      } else if (this.#text) {
        this.#text = undefined;
      }
    }
  };

  get value(): string {
    if (!this.#text) {
      return '';
    }

    return this.#text.toJSON();
  }

  get isEmpty() {
    // Containing only whitespaces in a text value is considered empty
    return !this.#text?.length || this.#text.toJSON().trim() === '';
  }

  insert(index: number, content: string): void {
    if (!this.#text) {
      this.#text = new Y.Text();
      this.input.set(ValueProperties.value, this.#text);
      this.attach?.(this.input);
    }

    this.#text.insert(index, content);
  }

  delete(index: number, length: number): void {
    if (!this.#text) {
      this.#text = new Y.Text();
      this.input.set(ValueProperties.value, this.#text);
      this.attach?.(this.input);
    }

    this.#text.delete(index, length);
  }

  applyDelta(delta: Op[]): void {
    if (!this.#text) {
      this.#text = new Y.Text();
      this.input.set(ValueProperties.value, this.#text);
      this.attach?.(this.input);
    }

    this.#text.applyDelta(delta);
  }

  get type(): FieldType.Text {
    return FieldType.Text;
  }

  toJSON(): ValueJSON<FieldType.Text, string> {
    return {
      type: this.type,
      value: this.value,
    };
  }

  static fromJSON(json: ValueJSON<FieldType.Text, string>) {
    return new YTextValue(
      new Y.Map([
        [ValueProperties.type, YFieldType.TEXT],
        [ValueProperties.value, new Y.Text(json.value)],
      ])
    );
  }

  unwrap(): Y.AbstractType<any> {
    return this.input;
  }

  dispose(): void {
    this.input.unobserve(this.onValueChanged);
  }
}
